import {QuotationRequest} from '../../model/QuotationRequest';
import {QuotationStep} from '../../model/QuotationStep';
import {QuotationType} from '../../model/QuotationType';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {QuickQuoteOutput} from '../../model/quotes/QuickQuoteOutput';
import {timeout} from 'rxjs/operators';
import {AddressRequest} from "../../model/AddressRequest";
import {ModelRequest} from "../../model/ModelRequest";
import {SelectListItem} from "../../model/SelectListItem";
import {ClientDetails} from "../../model/ClientDetails";
import {QuoteResponse} from "../../model/QuoteResponse";
import {GetQuoteVehicleAndDriverDetails} from "../../model/GetQuoteVehicleAndDriverDetails";
import {VehicleInformation} from "../../api/models/vehicle-information";

@Injectable()
export class QuotationService {
  public isIndicativeOrInvalid = false;
  public fallBackError = 'A technical error has occurred. An agent will call you back.';
  private _vehicleTypes: string[] = ['Select Type'];
  private _vehicleColours: string[] = ['Select Colour'];
  private _vehicleSearchRetries = 2;
  private _quotationRequest: QuotationRequest = new QuotationRequest();
  private _quotationsForClient: QuickQuoteOutput = new QuickQuoteOutput();
  private _quotationStep: QuotationStep = QuotationStep.LANDING;
  private _quotationType: QuotationType = QuotationType.CAR_AND_HOME;
  private _error = false;
  private _errorMessage: string = null;
  private _errorCode: string = null;
  private _licenseFound = false;
  private _platform: string = null;
  private _apiURL: string = environment.apiUrl;
  private _timeout: number = 15000;
  private _campaign: string = null;
  private _showCallBack = false;
  private _isAfrikaansPage = false;
  private _addressRequest: AddressRequest = new AddressRequest();
  private _modelRequest: ModelRequest = new ModelRequest();
  private _yearsAtPreviousInsurerSelectItemList: SelectListItem[];
  private _genderSelectItemList: SelectListItem[];
  private _parkingSecuritySelectItemList: SelectListItem[];
  private _driversLicenseTypeSelectItemList: SelectListItem[];
  private _getQuoteVehicleAndDriverDetails: GetQuoteVehicleAndDriverDetails = new GetQuoteVehicleAndDriverDetails();
  private _quoteResponse: QuoteResponse;
  private _captureLicenseDetails = false;
  private _quoteInvalidOrIndicative = false;
  private _maxDateOfBirth: string;
  private _quoteActivationReferred = false;
  private _registeredOwner: string = null;
  private _leadUpdated: boolean = false;
  private _vehicleInfo: VehicleInformation;

  constructor(private http: HttpClient) {
    this.setRetries(this._vehicleSearchRetries);
  }

  get campaign(): string {
    return this._campaign;
  }

  set campaign(value: string) {
    this._campaign = value;
  }

  get timeout(): number {
    return this._timeout;
  }

  set timeout(value: number) {
    this._timeout = value;
  }

  get vehicleSearchRetries(): number {
    return this._vehicleSearchRetries;
  }

  set vehicleSearchRetries(value: number) {
    this._vehicleSearchRetries = value;
  }

  get vehicleTypes(): string[] {
    return this._vehicleTypes;
  }

  set vehicleTypes(value: string[]) {
    this._vehicleTypes = value;
  }

  get vehicleColours(): string[] {
    return this._vehicleColours;
  }

  set vehicleColours(value: string[]) {
    this._vehicleColours = value;
  }

  get licenseFound(): boolean {
    return this._licenseFound;
  }

  set licenseFound(value: boolean) {
    this._licenseFound = value;
  }

  get apiURL(): string {
    return this._apiURL;
  }

  set apiURL(value: string) {
    this._apiURL = value;
  }

  get errorCode(): string {
    return this._errorCode;
  }

  set errorCode(value: string) {
    this._errorCode = value;
  }

  get errorMessage(): string {
    return this._errorMessage;
  }

  set errorMessage(value: string) {
    this._errorMessage = value;
  }

  get error(): boolean {
    return this._error;
  }

  set error(value: boolean) {
    this._error = value;
  }

  get platform(): string {
    return this._platform;
  }

  set platform(value: string) {
    this._platform = value;
  }

  get quotationStep(): QuotationStep {
    return this._quotationStep;
  }

  set quotationStep(value: QuotationStep) {
    this._quotationStep = value;
  }

  get quotationType(): QuotationType {
    return this._quotationType;
  }

  set quotationType(value: QuotationType) {
    this._quotationType = value;
  }

  get quotationRequest(): QuotationRequest {
    return this._quotationRequest;
  }

  set quotationRequest(value: QuotationRequest) {
    this._quotationRequest = value;
  }

  get quotationsForClient(): any {
    return this._quotationsForClient;
  }

  set quotationsForClient(value: any) {
    this._quotationsForClient = value;
  }

  get leadUpdated(): boolean {
    return this._leadUpdated;
  }

  set leadUpdated(value: boolean) {
    this._leadUpdated = value;
  }

  public parseError(error1) {
    if (error1['name'] === 'TimeoutError') {
      this.errorCode = '99';
      return false;
    } else if (error1['error'] === undefined || error1['error'].toString().length > 2) {
      this.setFallbackError();
      return true;
    } else {
      this.errorCode = error1['error'];
    }
    return false;
  }

  public requestVehicleDetails(): Observable<Object> {
    // return this.http.get('././assets/vehicleDataMock.json');
    return this.http.post(this.apiURL + '/vehicleLookup',
      JSON.stringify(this.quotationRequest), {headers: environment.plainHeaders}).pipe(timeout(this.timeout));
  }

  public generateQuotes(): Observable<Object> {
    // return this.http.get('././assets/generateQuotesMock.json');
    return this.http.post(this.apiURL + '/createQuote', JSON.stringify(this.quotationRequest),
      {headers: environment.plainHeaders});
  }

  public getLoggedInUser() {
    return this.http.get(this.apiURL + '/loggedInUser');
  }

  public getErrorList() {
    return this.http.get(this.apiURL + '/errorList');
  }

  public setRetries(retries: number) {
    localStorage.setItem('retries', retries.toString());
  }

  public getRetries(): number {
    return parseInt(localStorage.getItem('retries'), 10);
  }

  public reduceRetries() {
    let retries = this.getRetries();

    if (retries === 0) {
      this.errorMessage = 'Our system could not find your details. An agent will call you back.';
      this.error = true;
      return;
    }
    retries = retries - 1;
    this.setRetries(retries);
  }

  public getAddressList() {
    return this.http.post(this.apiURL + '/addressLookup', this.addressRequest, {headers: environment.plainHeaders});
  }

  public getVehicleSearchOptions() {
    return this.http.post(this.apiURL + '/modelRangeYearLookup', this.modelRequest, {headers: environment.plainHeaders});
  }

  public getTimeoutTime() {
    return this.http.get(this.apiURL + '/timeout', {headers: environment.plainHeaders});
  }

  public scrollToTop() {
    window.scrollTo({behavior: 'smooth', top: 0});
    window.parent.scrollTo({behavior: 'smooth', top: 0});
  }

  public scrollToPosition(verticalPosition) {
    window.parent.scrollTo({behavior: 'smooth', top: verticalPosition});
  }

  setFallbackError() {
    this.errorMessage = this.fallBackError;
    this.errorCode = null;
    this.error = true;
  }

  getVehicleColours() {
    return this.http.get(this.apiURL + '/getVehicleColorList', {headers: environment.plainHeaders});
  }

  getLicenseTypes() {
    return this.http.get(this.apiURL + '/getLicenseTypesList', {headers: environment.plainHeaders});
  }

  getAiQouteRoute() {
    return this.http.get(this.apiURL + '/getAiQuoteRoute', {headers: environment.plainHeaders});
  }

  getEarlyExitReasons() {
    return this.http.get(this.apiURL + '/getEarlyExitReasons', {headers: environment.plainHeaders});
  }

  get showCallBack(): boolean {
    return this._showCallBack;
  }

  set showCallBack(value: boolean) {
    this._showCallBack = value;
  }

  get isAfrikaansPage(): boolean {
    return this._isAfrikaansPage;
  }

  set isAfrikaansPage(value: boolean) {
    this._isAfrikaansPage = value;
  }

  get addressRequest(): AddressRequest {
    return this._addressRequest;
  }

  set addressRequest(value: AddressRequest) {
    this._addressRequest = value;
  }

  get modelRequest(): ModelRequest {
    return this._modelRequest;
  }

  set modelRequest(value: ModelRequest) {
    this._modelRequest = value;
  }

  get yearsAtPreviousInsurerSelectItemList(): SelectListItem[] {
    return this._yearsAtPreviousInsurerSelectItemList;
  }

  set yearsAtPreviousInsurerSelectItemList(value: SelectListItem[]) {
    this._yearsAtPreviousInsurerSelectItemList = value;
  }

  get genderSelectItemList(): SelectListItem[] {
    return this._genderSelectItemList;
  }

  set genderSelectItemList(value: SelectListItem[]) {
    this._genderSelectItemList = value;
  }

  get driversLicenseTypeSelectItemList(): SelectListItem[] {
    return this._driversLicenseTypeSelectItemList;
  }

  set driversLicenseTypeSelectItemList(value: SelectListItem[]) {
    this._driversLicenseTypeSelectItemList = value;
  }
  get parkingSecuritySelectItemList(): SelectListItem[] {
    return this._parkingSecuritySelectItemList;
  }

  set parkingSecuritySelectItemList(value: SelectListItem[]) {
    this._parkingSecuritySelectItemList = value;
  }

  get getQuoteVehicleAndDriverDetails(): GetQuoteVehicleAndDriverDetails {
    return this._getQuoteVehicleAndDriverDetails;
  }

  set getQuoteVehicleAndDriverDetails(value: GetQuoteVehicleAndDriverDetails) {
    this._getQuoteVehicleAndDriverDetails = value;
  }

  get quoteResponse(): QuoteResponse {
    return this._quoteResponse;
  }

  set quoteResponse(value: QuoteResponse) {
    this._quoteResponse = value;
  }
  
  get vehicleInfo(): VehicleInformation {
    return this._vehicleInfo;
  }

  set vehicleInfo(value: VehicleInformation) {
    this._vehicleInfo = value;
  }

  get captureLicenseDetails(): boolean {
    return this._captureLicenseDetails;
  }

  set captureLicenseDetails(value: boolean) {
    this._captureLicenseDetails = value;
  }

  get quoteInvalidOrIndicative(): boolean {
    return this._quoteInvalidOrIndicative;
  }

  set quoteInvalidOrIndicative(value: boolean) {
    this._quoteInvalidOrIndicative = value;
  }

  get maxDateOfBirth(): string {
    return this._maxDateOfBirth;
  }

  set maxDateOfBirth(value: string) {
    this._maxDateOfBirth = value;
  }

  get quoteActivationReferred(): boolean {
    return this._quoteActivationReferred;
  }

  set quoteActivationReferred(value: boolean) {
    this._quoteActivationReferred = value;
  }

  get registeredOwner(): string {
    return this._registeredOwner;
  }

  set registeredOwner(value: string) {
    this._registeredOwner = value;
  }

  public loadScreen(journeyStep) {
    return this.http.get(this.apiURL + '/loadScreen/' + journeyStep, {headers: environment.plainHeaders});
  }

  public checkQuoteEligibility(clientDetails: ClientDetails): Observable<Object> {
    // return this.http.get('././assets/vehicleDataMock.json');
    return this.http.post(this.apiURL + '/checkQuoteEligibility',
      JSON.stringify(clientDetails), {headers: environment.plainHeaders});
  }

  public searchVehicleByRegOrVin(request): Observable<Object> {
    // return this.http.get('././assets/vehicleDataMock.json');
    return this.http.post(this.apiURL + '/vehicleSearchReg',
      JSON.stringify(request), {headers: environment.plainHeaders});
  }

  public searchVehicleByMakeModelYear(request): Observable<Object> {
    // return this.http.get('././assets/vehicleDataMock.json');
    return this.http.post(this.apiURL + '/vehicleAutoComplete',
      JSON.stringify(request), {headers: environment.plainHeaders});
  }

  public searchSuburb(searchTerm: String) {
    return this.http.post(this.apiURL + '/searchSuburb/' + searchTerm, {headers: environment.plainHeaders});
  }

  public getQuote() {
    return this.http.post(this.apiURL + '/getQuote',
      JSON.stringify(this.getQuoteVehicleAndDriverDetails), {headers: environment.plainHeaders});
  }

  public getActivateButtonStatus() {
    return this.http.get(this.apiURL + '/getActivateButtonStatus', {headers: environment.plainHeaders});
  }

  public getExistingClientLoginRoute() {
    return this.http.get(this.apiURL + '/getExistingClientLoginRoute', {headers: environment.plainHeaders, responseType: 'text'});
  }

  public getRegistrationUrl() {
    return this.http.get(this.apiURL + '/getRegistrationUrl', {headers: environment.plainHeaders, responseType: 'text'});
  }
}
